import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Card,
  Heading,
  Table,
  TextField,
  Badge,
  Dialog,
  Select,
  Checkbox,
  AlertDialog
} from '@radix-ui/themes';
import { Pencil1Icon, ReloadIcon, MixerHorizontalIcon, PersonIcon, ArchiveIcon, PlusIcon } from '@radix-ui/react-icons';
import { toast } from 'react-hot-toast';
import AdminLayout from './AdminLayout';
import { adminApi } from '../../services/api';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useOrganization } from '../../contexts/OrganizationContext';

interface AdminOrganizationOption {
  id: string;
  name: string;
  ownerUserId?: string | null;
}

interface AdminUserOrganization {
  id: string;
  name: string;
}

interface AdminUser {
  id: string;
  email: string;
  name?: string | null;
  organizations: AdminUserOrganization[];
  roles: string[];
  isInternal: boolean;
  archived?: boolean | null;
}

interface RoleOption {
  id: string;
  label: string;
}

const normalizeUsers = (data: any): AdminUser[] => {
  const records = data?.records || data?.users || data || [];
  if (!Array.isArray(records)) return [];
  return records.map((record: any) => ({
    id: String(record.id ?? record.user_id ?? record.userId ?? record.sub),
    email: record.email ?? record.user_email ?? 'unknown',
    name: record.name ?? record.full_name ?? null,
    organizations: (record.organizations || record.orgs || record.organizations_list || []).map((org: any) => ({
      id: String(org.id ?? org.organization_id ?? org.organizationId ?? org.org_id),
      name: org.name ?? org.organization_name ?? 'Unnamed',
    })),
    roles: Array.isArray(record.roles)
      ? record.roles
      : record.role
        ? [record.role]
        : [],
    isInternal: Boolean(record.is_internal ?? record.internal ?? false),
    archived: typeof record.archived !== 'undefined'
      ? record.archived
      : typeof record.is_archived !== 'undefined'
        ? record.is_archived
        : record.archived_at
          ? true
          : null,
  }));
};

const normalizeOrganizations = (data: any): AdminOrganizationOption[] => {
  const records = data?.records || data?.organizations || data || [];
  if (!Array.isArray(records)) return [];
  return records.map((record: any) => ({
    id: String(record.id ?? record.organization_id ?? record.organizationId ?? record.org_id),
    name: record.name ?? record.organization_name ?? 'Unnamed Clinic',
    ownerUserId: record.owner ?? record.owner_user_id ?? record.ownerUserId ?? record.owner_id ?? null,
  }));
};

const roleLabels: Record<string, string> = {
  clinical_care_manager: 'Clinical Care Manager',
  billing_operations_manager: 'Billing & Operation Manager',
  clinical_medical_director: 'Clinical Medical Director',
  // PHA-1240: designs Custom Reports / Views in the user's clinics.
  report_designer: 'Report designer',
};

// PHA-219: clinic-assignable roles that the admin tool toggles via checkboxes.
const ASSIGNABLE_ROLE_IDS = [
  'clinical_care_manager',
  'billing_operations_manager',
  'clinical_medical_director',
  'report_designer',
];


const AdminUsersPage: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [organizations, setOrganizations] = useState<AdminOrganizationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // PHA-527: org-state from OrganizationContext.
  const { orgId: ctxOrgId, loading: orgLoading } = useOrganization();
  const sessionOrgId: string | null = ctxOrgId || null;
  const sessionLoaded = !orgLoading;

  const [manageUser, setManageUser] = useState<AdminUser | null>(null);
  const [editUser, setEditUser] = useState<AdminUser | null>(null);
  const [assignRoleUser, setAssignRoleUser] = useState<AdminUser | null>(null);
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);
  const [ownerOrgId, setOwnerOrgId] = useState<string>('');
  const [initialOwnerOrgId, setInitialOwnerOrgId] = useState<string>('');
  const [roleSelection, setRoleSelection] = useState<string[]>([]);
  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [roleSaving, setRoleSaving] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', name: '' });
  const [inviteOrgIds, setInviteOrgIds] = useState<string[]>([]);
  const [inviteRoleIds, setInviteRoleIds] = useState<string[]>([]);
  const [inviteUseSSO, setInviteUseSSO] = useState(false);
  const [inviteLinkOpen, setInviteLinkOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteLinkEmail, setInviteLinkEmail] = useState('');
  const [inviteLinkIsSSO, setInviteLinkIsSSO] = useState(false);
  const [inviteLinkLoading, setInviteLinkLoading] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<AdminUser | null>(null);

  const debouncedSearch = useDebouncedValue(search, 300);

  const loadOrganizations = useCallback(async () => {
    try {
      const data = await adminApi.getOrganizations();
      setOrganizations(normalizeOrganizations(data));
    } catch (err: any) {
      console.error('Failed to load organizations:', err);
    }
  }, []);

  const loadRoles = useCallback(async () => {
    try {
      const data = await adminApi.getAvailableRoles();
      const records = data?.records || data?.roles || data || [];
      const options: RoleOption[] = Array.isArray(records)
        ? records.map((role: any) => ({
            id: role.id ?? role.name ?? role.role_id ?? role.role,
            label: roleLabels[role.name] || role.label || role.name || role.id,
          }))
        : [];

      if (options.length === 0) {
        setRoleOptions([
          { id: 'clinical_care_manager', label: roleLabels.clinical_care_manager },
          { id: 'billing_operations_manager', label: roleLabels.billing_operations_manager },
        ]);
      } else {
        setRoleOptions(options);
      }
    } catch (err: any) {
      console.error('Failed to load roles:', err);
      setRoleOptions([
        { id: 'clinical_care_manager', label: roleLabels.clinical_care_manager },
        { id: 'billing_operations_manager', label: roleLabels.billing_operations_manager },
      ]);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: { organization_id?: string; search?: string } = {};
      if (sessionOrgId && sessionOrgId !== 'ALL') {
        params.organization_id = sessionOrgId;
      }
      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }
      const data = await adminApi.getUsers(params);
      setUsers(normalizeUsers(data));
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [sessionOrgId, debouncedSearch]);

  useEffect(() => {
    loadOrganizations();
    loadRoles();
  }, [loadOrganizations, loadRoles]);

  useEffect(() => {
    if (!sessionLoaded) return;
    loadUsers();
  }, [sessionLoaded, loadUsers]);

  const openManageOrgs = (user: AdminUser) => {
    setManageUser(user);
    setSelectedOrgIds(user.organizations.map((org) => org.id));
    const ownerOrg = organizations.find((org) => org.ownerUserId === user.id);
    const ownerId = ownerOrg?.id || '';
    setOwnerOrgId(ownerId);
    setInitialOwnerOrgId(ownerId);
  };

  const openEditUser = (user: AdminUser) => {
    setEditUser(user);
  };

  const openAssignRole = (user: AdminUser) => {
    setAssignRoleUser(user);
    setRoleSelection((user.roles || []).filter((role) => ASSIGNABLE_ROLE_IDS.includes(role)));
  };

  const openInviteUser = () => {
    setInviteForm({ email: '', name: '' });
    setInviteOrgIds([]);
    setInviteRoleIds(['clinical_care_manager']);
    setInviteUseSSO(false);
    setInviteOpen(true);
  };

  const toggleRoleSelection = (roleId: string) => {
    setRoleSelection((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const toggleInviteRoleSelection = (roleId: string) => {
    setInviteRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : [...prev, roleId]
    );
  };

  const toggleOrgSelection = (orgId: string) => {
    setSelectedOrgIds((prev) =>
      prev.includes(orgId) ? prev.filter((id) => id !== orgId) : [...prev, orgId]
    );
  };

  const toggleInviteOrgSelection = (orgId: string) => {
    setInviteOrgIds((prev) =>
      prev.includes(orgId) ? prev.filter((id) => id !== orgId) : [...prev, orgId]
    );
  };

  const handleSaveOrganizations = async () => {
    if (!manageUser) return;
    setSaving(true);
    try {
      const originalOrgIds = manageUser.organizations.map((org) => org.id);
      const toAdd = selectedOrgIds.filter((id) => !originalOrgIds.includes(id));
      const toRemove = originalOrgIds.filter((id) => !selectedOrgIds.includes(id));

      if (toRemove.length > 0) {
        const confirmed = window.confirm(`Remove user from ${toRemove.length} organization(s)?`);
        if (!confirmed) {
          setSaving(false);
          return;
        }
      }

      await Promise.all([
        ...toAdd.map((orgId) => adminApi.addUserToOrg(manageUser.id, orgId)),
        ...toRemove.map((orgId) => adminApi.removeUserFromOrg(manageUser.id, orgId)),
      ]);

      // Update clinic admin (owner) status
      if (ownerOrgId && ownerOrgId !== initialOwnerOrgId) {
        // New org selected or transferred
        await adminApi.updateOrganization(ownerOrgId, { owner: manageUser.id });
      } else if (ownerOrgId === '' && initialOwnerOrgId) {
        // Ownership was removed (dropdown was set to "remove" = empty string)
        await adminApi.updateOrganization(initialOwnerOrgId, { owner: '' });
      }

      toast.success('Organizations updated');
      setManageUser(null);
      loadUsers();
      loadOrganizations();
    } catch (err: any) {
      console.error('Failed to update organizations:', err);
      toast.error(err.message || 'Failed to update organizations');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignRole = async () => {
    if (!assignRoleUser) return;
    if (roleSelection.length === 0) {
      toast.error('Select at least one role');
      return;
    }
    setRoleSaving(true);
    try {
      const existing = (assignRoleUser.roles || []).filter((role) => ASSIGNABLE_ROLE_IDS.includes(role));
      const desired = roleSelection;
      const toAdd = desired.filter((role) => !existing.includes(role));
      const toRemove = existing.filter((role) => !desired.includes(role));

      await Promise.all([
        ...toRemove.map((role) => adminApi.removeRole(assignRoleUser.id, role)),
        ...toAdd.map((role) => adminApi.assignRole(assignRoleUser.id, role)),
      ]);

      toast.success('Roles updated');
      setAssignRoleUser(null);
      loadUsers();
    } catch (err: any) {
      console.error('Failed to assign roles:', err);
      toast.error(err.message || 'Failed to assign roles');
    } finally {
      setRoleSaving(false);
    }
  };

  const openInviteLinkDialog = (email: string, link: string, isSSO: boolean = false) => {
    setInviteLinkEmail(email);
    setInviteLink(link);
    setInviteLinkIsSSO(isSSO);
    setInviteLinkOpen(true);
  };

  const copyInviteLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Invite link copied');
    } catch (err: any) {
      console.error('Failed to copy invite link:', err);
      toast.error('Failed to copy invite link');
    }
  };

  const handleResendInvite = async (user: AdminUser) => {
    setInviteLinkLoading(true);
    try {
      const result = await adminApi.createInviteLink(user.id);
      const link = result?.invite_link || result?.login_url;
      const isSSO = !!result?.sso || !!result?.login_url;
      if (link) {
        openInviteLinkDialog(user.email, link, isSSO);
        await copyInviteLink(link);
      } else {
        toast.error('Invite link not available');
      }
    } catch (err: any) {
      console.error('Failed to create invite link:', err);
      toast.error(err.message || 'Failed to create invite link');
    } finally {
      setInviteLinkLoading(false);
    }
  };

  const handleInviteUser = async () => {
    const email = inviteForm.email.trim();
    if (!email) {
      toast.error('Email is required');
      return;
    }
    if (inviteRoleIds.length === 0) {
      toast.error('Please select at least one role');
      return;
    }
    setInviteSaving(true);
    try {
      const result = await adminApi.createUser({
        email,
        name: inviteForm.name.trim() || undefined,
        use_sso: inviteUseSSO,
      });
      const createdUserId =
        result?.user?.id ?? result?.user_id ?? result?.id ?? null;
      if (createdUserId) {
        await Promise.all([
          ...inviteOrgIds.map((orgId) => adminApi.addUserToOrg(createdUserId, orgId)),
          ...inviteRoleIds.map((roleId) => adminApi.assignRole(createdUserId, roleId)),
        ]);
      }
      if (result?.invite_link || result?.login_url) {
        const link = result.invite_link || result.login_url;
        openInviteLinkDialog(email, link, inviteUseSSO);
        await copyInviteLink(link);
      } else {
        toast.success('User created');
      }
      setInviteOpen(false);
      loadUsers();
      loadOrganizations();
    } catch (err: any) {
      console.error('Failed to invite user:', err);
      toast.error(err.message || 'Failed to invite user');
    } finally {
      setInviteSaving(false);
    }
  };

  const handleArchiveUser = async () => {
    if (!archiveTarget) return;
    const nextArchived = !archiveTarget.archived;
    try {
      await adminApi.updateUser(archiveTarget.id, { archived: nextArchived });
      toast.success(nextArchived ? 'User archived' : 'User unarchived');
      setArchiveTarget(null);
      loadUsers();
    } catch (err: any) {
      console.error('Failed to update user:', err);
      toast.error(err.message || 'Failed to update user');
    }
  };

  const roleBadge = (roles: string[]) => {
    if (!roles || roles.length === 0) return '—';
    const known = roles.filter((role) => roleLabels[role]);
    const display = known.length > 0 ? known : [roles[0]];
    return display.map((role) => roleLabels[role] || role).join(', ');
  };

  const filteredOrganizations = useMemo(() => {
    return organizations.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [organizations]);

  return (
    <AdminLayout
      title="Users Management"
      subtitle="Assign clinics, set roles, and review internal access."
    >
      <Card className="mb-4">
        <Box p="4">
          <Flex align="center" justify="between" gap="3" wrap="wrap">
            <Flex gap="2" wrap="wrap">
              <TextField.Root style={{ minWidth: 240 }}>
                <TextField.Input
                  placeholder="Search by name or email"
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                />
              </TextField.Root>
            </Flex>
            <Flex align="center" gap="2">
              <Button variant="soft" onClick={loadUsers} disabled={loading}>
                <ReloadIcon className={loading ? 'animate-spin' : ''} />
                Refresh
              </Button>
              <Button variant="solid" onClick={openInviteUser}>
                <PlusIcon />
                Invite User
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Card>

      <Card>
        <Box p="4">
          <Heading size="4" className="mb-3">All Users</Heading>
          {error && (
            <Text color="red" size="2" className="mb-3 block">{error}</Text>
          )}
          {loading && users.length === 0 ? (
            <Flex justify="center" align="center" style={{ minHeight: 120 }}>
              <ReloadIcon className="animate-spin" width="24" height="24" />
            </Flex>
          ) : (
            <Table.Root variant="surface">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Organizations</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Role</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Internal</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {users.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={7}>
                      <Text color="gray">No users found.</Text>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  users.map((user) => (
                    <Table.Row key={user.id}>
                      <Table.Cell>
                        <Text size="2" weight="medium">{user.email}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">{user.name || '—'}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Flex gap="2" wrap="wrap">
                          {user.organizations.length === 0 ? (
                            <Text size="1" color="gray">No orgs</Text>
                          ) : (
                            user.organizations.map((org) => (
                              <Badge key={org.id} color="gray">{org.name}</Badge>
                            ))
                          )}
                        </Flex>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">{roleBadge(user.roles)}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge color={user.isInternal ? 'green' : 'gray'}>
                          {user.isInternal ? 'Internal' : 'External'}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge color={user.archived ? 'red' : 'gray'}>
                          {user.archived ? 'Archived' : 'Active'}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Flex gap="2" wrap="wrap">
                          <Button variant="soft" onClick={() => openEditUser(user)} disabled={!!user.archived}>
                            <Pencil1Icon />
                            Edit
                          </Button>
                          <Button variant="soft" onClick={() => openManageOrgs(user)} disabled={!!user.archived}>
                            <MixerHorizontalIcon />
                            Manage Orgs
                          </Button>
                          <Button variant="soft" onClick={() => openAssignRole(user)} disabled={!!user.archived}>
                            <PersonIcon />
                            Assign Role
                          </Button>
                          <Button
                            variant="soft"
                            onClick={() => handleResendInvite(user)}
                            disabled={!!user.archived || inviteLinkLoading}
                          >
                            <PlusIcon />
                            Invite Link
                          </Button>
                          <Button
                            variant={user.archived ? 'soft' : 'solid'}
                            color={user.archived ? 'green' : 'red'}
                            onClick={() => setArchiveTarget(user)}
                          >
                            <ArchiveIcon />
                            {user.archived ? 'Unarchive' : 'Archive'}
                          </Button>
                        </Flex>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Root>
          )}
        </Box>
      </Card>

      <Dialog.Root open={!!manageUser} onOpenChange={(open) => !open && setManageUser(null)}>
        <Dialog.Content style={{ maxWidth: 560 }}>
          <Dialog.Title>Manage User Organizations</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            Add or remove organizations for {manageUser?.email}.
          </Dialog.Description>
          <Flex direction="column" gap="3" className="mt-4">
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">Organizations</Text>
              <Flex direction="column" gap="2">
                {filteredOrganizations.map((org) => (
                  <label key={org.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedOrgIds.includes(org.id)}
                      onCheckedChange={() => toggleOrgSelection(org.id)}
                    />
                    <Text size="2">{org.name}</Text>
                  </label>
                ))}
              </Flex>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">Set as Clinic Admin</Text>
              <Select.Root
                value={ownerOrgId === '' ? 'remove' : (ownerOrgId || 'none')}
                onValueChange={(value) => setOwnerOrgId(value === 'remove' ? '' : value === 'none' ? '' : value)}
              >
                <Select.Trigger placeholder="Select organization" />
                <Select.Content>
                  <Select.Item value="none">No change</Select.Item>
                  <Select.Item value="remove">Remove as Clinic Admin</Select.Item>
                  {selectedOrgIds.map((orgId) => {
                    const org = organizations.find((item) => item.id === orgId);
                    return org ? (
                      <Select.Item key={org.id} value={org.id}>
                        {org.name}
                      </Select.Item>
                    ) : null;
                  })}
                </Select.Content>
              </Select.Root>
            </Box>
          </Flex>
          <Flex justify="end" gap="2" className="mt-4">
            <Dialog.Close>
              <Button variant="soft" type="button">Cancel</Button>
            </Dialog.Close>
            <Button variant="solid" onClick={handleSaveOrganizations} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <Dialog.Root open={!!assignRoleUser} onOpenChange={(open) => !open && setAssignRoleUser(null)}>
        <Dialog.Content style={{ maxWidth: 480 }}>
          <Dialog.Title>Assign Roles</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            Select one or more roles for {assignRoleUser?.email}. Users may hold multiple roles
            (e.g. lead physician + biller).
          </Dialog.Description>
          <Box className="mt-4">
            <Flex direction="column" gap="3">
              {roleOptions.map((role) => (
                <label key={role.id} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={roleSelection.includes(role.id)}
                    onCheckedChange={() => toggleRoleSelection(role.id)}
                  />
                  <Text size="2">{role.label}</Text>
                </label>
              ))}
            </Flex>
          </Box>
          <Flex justify="end" gap="2" className="mt-4">
            <Dialog.Close>
              <Button variant="soft" type="button">Cancel</Button>
            </Dialog.Close>
            <Button variant="solid" onClick={handleAssignRole} disabled={roleSaving}>
              {roleSaving ? 'Saving...' : 'Save Roles'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <AlertDialog.Root open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <AlertDialog.Content style={{ maxWidth: 460 }}>
          <AlertDialog.Title>User Details</AlertDialog.Title>
          <AlertDialog.Description size="2" color="gray">
            Review user information. Profile edits are managed via authentication provider.
          </AlertDialog.Description>
          <Box className="mt-4">
            <Flex direction="column" gap="2">
              <Text size="2"><strong>Email:</strong> {editUser?.email}</Text>
              <Text size="2"><strong>Name:</strong> {editUser?.name || '—'}</Text>
              <Text size="2"><strong>Role:</strong> {editUser ? roleBadge(editUser.roles) : '—'}</Text>
              <Text size="2"><strong>Internal:</strong> {editUser?.isInternal ? 'Yes' : 'No'}</Text>
            </Flex>
          </Box>
          <Flex justify="end" gap="2" className="mt-4">
            <AlertDialog.Cancel>
              <Button variant="soft">Close</Button>
            </AlertDialog.Cancel>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      <Dialog.Root open={inviteOpen} onOpenChange={setInviteOpen}>
        <Dialog.Content style={{ maxWidth: 520 }}>
          <Dialog.Title>Invite User</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            Create a new user, assign clinics and role, then share the invite link.
          </Dialog.Description>
          <Flex direction="column" gap="3" className="mt-4">
            <TextField.Root>
              <TextField.Input
                placeholder="Email address"
                value={inviteForm.email}
                onChange={(e) => setInviteForm((prev) => ({ ...prev, email: e?.target?.value ?? '' }))}
              />
            </TextField.Root>
            <TextField.Root>
              <TextField.Input
                placeholder="Full name (optional)"
                value={inviteForm.name}
                onChange={(e) => setInviteForm((prev) => ({ ...prev, name: e?.target?.value ?? '' }))}
              />
            </TextField.Root>
            <Box>
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={inviteUseSSO}
                  onCheckedChange={(checked) => setInviteUseSSO(checked === true)}
                />
                <Text size="2">Use SSO (Google/Microsoft)</Text>
              </label>
              {inviteUseSSO && (
                <Text size="1" color="gray" className="mt-1 block">
                  User will sign in with their existing Google or Microsoft account. No password setup required.
                </Text>
              )}
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">Assign clinics</Text>
              <Flex direction="column" gap="2">
                {filteredOrganizations.map((org) => (
                  <label key={org.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={inviteOrgIds.includes(org.id)}
                      onCheckedChange={() => toggleInviteOrgSelection(org.id)}
                    />
                    <Text size="2">{org.name}</Text>
                  </label>
                ))}
              </Flex>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-2 block">Assign roles</Text>
              <Flex direction="column" gap="2">
                {roleOptions.map((role) => (
                  <label key={role.id} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={inviteRoleIds.includes(role.id)}
                      onCheckedChange={() => toggleInviteRoleSelection(role.id)}
                    />
                    <Text size="2">{role.label}</Text>
                  </label>
                ))}
              </Flex>
            </Box>
          </Flex>
          <Flex justify="end" gap="2" className="mt-4">
            <Dialog.Close>
              <Button variant="soft" type="button">Cancel</Button>
            </Dialog.Close>
            <Button variant="solid" onClick={handleInviteUser} disabled={inviteSaving}>
              {inviteSaving ? 'Creating...' : 'Create & Invite'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <Dialog.Root open={inviteLinkOpen} onOpenChange={setInviteLinkOpen}>
        <Dialog.Content style={{ maxWidth: 520 }}>
          <Dialog.Title>{inviteLinkIsSSO ? 'SSO User Created' : 'Invite Link'}</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            {inviteLinkIsSSO
              ? `Share this login URL with ${inviteLinkEmail || 'the user'}. They can sign in using their Google or Microsoft account.`
              : `Share this link with ${inviteLinkEmail || 'the user'} to set their password.`}
          </Dialog.Description>
          <Flex direction="column" gap="3" className="mt-4">
            <TextField.Root>
              <TextField.Input value={inviteLink} readOnly />
            </TextField.Root>
            <Text size="2" color="gray">
              {inviteLinkIsSSO
                ? 'The user can log in immediately using SSO.'
                : 'Invite links expire. You can generate a fresh link anytime.'}
            </Text>
          </Flex>
          <Flex justify="end" gap="2" className="mt-4">
            <Button variant="soft" onClick={() => copyInviteLink(inviteLink)} disabled={!inviteLink}>
              Copy Link
            </Button>
            <Button
              variant="solid"
              onClick={() => inviteLink && window.open(inviteLink, '_blank', 'noopener')}
              disabled={!inviteLink}
            >
              Open Link
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <AlertDialog.Root open={!!archiveTarget} onOpenChange={(open) => !open && setArchiveTarget(null)}>
        <AlertDialog.Content style={{ maxWidth: 460 }}>
          <AlertDialog.Title>
            {archiveTarget?.archived ? 'Unarchive User' : 'Archive User'}
          </AlertDialog.Title>
          <AlertDialog.Description size="2" color="gray">
            {archiveTarget?.archived
              ? `Restore access for ${archiveTarget?.email}?`
              : `Archive ${archiveTarget?.email}? This will block access and mark the user as archived.`}
          </AlertDialog.Description>
          <Flex justify="end" gap="2" className="mt-4">
            <AlertDialog.Cancel>
              <Button variant="soft">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button variant="solid" color="red" onClick={handleArchiveUser}>
                {archiveTarget?.archived ? 'Unarchive' : 'Archive'}
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </AdminLayout>
  );
};

export default AdminUsersPage;
